// Sell's modifiers dialog (spec/13 §4, §6.1; F-FNB-01 rule 3): a product with modifier groups opens this before it
// joins the cart. A group that takes one choice is a radio group; one that takes several is a set of checkboxes.
// Each choice shows its price. Add stays disabled until every group is satisfied — a required group chosen, and
// none over its maximum — by `@clavian/domain`'s rule, the one the server refuses a cart by, so the counter never
// sends a line the server would turn away.

import type { MenuModifierGroup, MenuProduct, Money } from '@clavian/api-client';
import { modifierChoiceProblem, modifierGroupMinimum } from '@clavian/domain/menu-choice';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, RadioGroup } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

/** What a choice adds to the cart: the product's modifiers chosen, their names and their prices. */
export interface ModifierChoice {
  readonly modifierIds: readonly string[];
  readonly names: readonly string[];
  readonly extra: Money;
}

export function ModifierDialog({
  product,
  onAdd,
  onClose,
}: {
  /** The product being chosen for; `null` keeps the dialog closed. */
  readonly product: MenuProduct | null;
  readonly onAdd: (product: MenuProduct, choice: ModifierChoice) => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={product !== null} onOpenChange={(open) => (open ? undefined : onClose())} size="sm" eyebrow={t('sell.modifiers.eyebrow')} title={product?.name ?? ''}>
      {product === null ? null : <ModifierForm key={product.id} product={product} onAdd={(choice) => onAdd(product, choice)} onCancel={onClose} />}
    </Dialog>
  );
}

function ModifierForm({ product, onAdd, onCancel }: { readonly product: MenuProduct; readonly onAdd: (choice: ModifierChoice) => void; readonly onCancel: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const groups = product.modifierGroups.filter((group) => group.modifiers.length > 0);
  const [chosen, setChosen] = useState<Readonly<Record<string, readonly string[]>>>({});

  const allChosen = groups.flatMap((group) => chosen[group.id] ?? []);
  const problem = modifierChoiceProblem(
    groups.map((group) => ({ id: group.id, name: group.name, required: group.required, minChoices: group.minChoices, maxChoices: group.maxChoices, modifierIds: group.modifiers.map((modifier) => modifier.id) })),
    allChosen,
  );
  const modifiers = groups.flatMap((group) => group.modifiers);
  const picked = modifiers.filter((modifier) => allChosen.includes(modifier.id));
  const extra: Money = { amount: picked.reduce((sum, modifier) => sum + modifier.price.amount, 0), currency: product.price.currency };
  const total: Money = { amount: product.price.amount + extra.amount, currency: product.price.currency };

  const priced = (name: string, price: Money): string => (price.amount === 0 ? name : t('sell.modifiers.priced', { name, price: format.money(price) }));
  const set = (group: MenuModifierGroup, ids: readonly string[]) => setChosen((current) => ({ ...current, [group.id]: ids }));

  return (
    <form
      className="console-form console-sell-modifiers"
      aria-label={t('sell.modifiers.label', { name: product.name })}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (problem !== null) return;
        onAdd({ modifierIds: allChosen, names: picked.map((modifier) => modifier.name), extra });
      }}
    >
      {groups.map((group) => {
        const min = modifierGroupMinimum(group);
        const hint = min > 0 ? t('sell.modifiers.required', { count: Math.max(min, 1) }) : t('sell.modifiers.upTo', { count: group.maxChoices });
        const ids = chosen[group.id] ?? [];
        if (group.maxChoices === 1) {
          return (
            <RadioGroup
              key={group.id}
              label={group.name}
              description={hint}
              required={min > 0}
              value={ids[0] ?? ''}
              onValueChange={(value) => set(group, [value])}
              options={group.modifiers.map((modifier) => ({ value: modifier.id, label: priced(modifier.name, modifier.price), disabled: !modifier.available }))}
            />
          );
        }
        return (
          <fieldset key={group.id} className="console-sell-modifiers__group">
            <legend className="clav-field__label">{group.name}</legend>
            <p className="console-muted">{hint}</p>
            {group.modifiers.map((modifier) => (
              <Checkbox
                key={modifier.id}
                label={priced(modifier.name, modifier.price)}
                disabled={!modifier.available || (!ids.includes(modifier.id) && ids.length >= group.maxChoices)}
                checked={ids.includes(modifier.id)}
                onCheckedChange={(checked) => set(group, checked === true ? [...ids, modifier.id] : ids.filter((id) => id !== modifier.id))}
              />
            ))}
          </fieldset>
        );
      })}
      <p className="console-sell-modifiers__total" role="status">
        {problem === null ? t('sell.modifiers.total', { price: format.money(total) }) : t('sell.modifiers.choose', { group: problem.groupName })}
      </p>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={problem !== null}>
          {t('sell.modifiers.add')}
        </Button>
      </div>
    </form>
  );
}
